import { useState, useEffect, useRef, useCallback } from 'react';
import { TeamName, useScore } from '../../../context/ScoreContext';
import { useComments } from '../../../hooks/useComments';
import { supabase } from '../../../lib/supabase';

// ── Animated number hook ──────────────────────────────────────────────────────
function useAnimatedNumber(target: number) {
  const [display, setDisplay] = useState(target);
  const prevRef = useRef(target);

  useEffect(() => {
    const start = prevRef.current;
    const diff = target - start;
    if (diff === 0) return;
    const duration = 700;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + diff * eased));
      if (progress < 1) requestAnimationFrame(animate);
      else prevRef.current = target;
    };
    requestAnimationFrame(animate);
  }, [target]);

  return display;
}

// ── Online presence hook ──────────────────────────────────────────────────────
function useOnlineCount() {
  const { myMemberId } = useScore();
  const [count, setCount] = useState(0);

  useEffect(() => {
    // Use memberId as presence key so same member on multiple devices counts as 1
    const channelOptions = myMemberId
      ? { config: { presence: { key: myMemberId } } }
      : {};

    const channel = supabase.channel('srs-member-presence', channelOptions);

    const updateCount = () =>
      setCount(Object.keys(channel.presenceState()).length);

    channel
      .on('presence', { event: 'sync' }, updateCount)
      .on('presence', { event: 'join' }, updateCount)
      .on('presence', { event: 'leave' }, updateCount)
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Only track presence if user has joined the game as a member
          if (myMemberId) {
            await channel.track({ online_at: new Date().toISOString() });
          }
          updateCount();
        }
      });

    return () => {
      channel.untrack();
      supabase.removeChannel(channel);
    };
  }, [myMemberId]);

  // Immediately show 0 on game reset (before presence sync catches up)
  useEffect(() => {
    const handleReset = () => setCount(0);
    window.addEventListener('srs-rewards-reset', handleReset);
    return () => window.removeEventListener('srs-rewards-reset', handleReset);
  }, []);

  return count;
}

// ── Font constants ────────────────────────────────────────────────────────────
const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";
const FONT_HUD  = "'Orbitron', monospace";
const FONT_DECO = "'Cinzel', serif";

// ── Time formatter ────────────────────────────────────────────────────────────
function formatTime(ts: number) {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// ── System comment expiry ─────────────────────────────────────────────────────
const SYS_TTL  = 60_000;
const SYS_FADE = 10_000;

function isSysExpired(createdAt: number): boolean {
  return Date.now() - createdAt >= SYS_TTL;
}

function getSysOpacity(createdAt: number): number {
  const age = Date.now() - createdAt;
  if (age < SYS_TTL - SYS_FADE) return 1;
  if (age >= SYS_TTL) return 0;
  return 1 - (age - (SYS_TTL - SYS_FADE)) / SYS_FADE;
}

// ── System comment style detector ────────────────────────────────────────────
interface SysStyle { color: string; bg: string; border: string; icon: string; label: string; }

function getSysCommentStyle(message: string, displayName?: string): SysStyle {
  if (displayName && displayName.includes('系統')) {
    // 臨時任務已完成通知 — 綠色
    if (message.includes('臨時任務') && message.includes('已完成')) {
      return { color: '#7ec8a0', bg: '#7ec8a010', border: '#7ec8a035', icon: 'ri-checkbox-circle-line', label: '任務完成' };
    }
    // 臨時任務新增通知 — 紅色
    if (message.includes('臨時任務')) {
      return { color: '#e05555', bg: '#e0555510', border: '#e0555538', icon: 'ri-alarm-warning-line', label: '臨時任務' };
    }
    return { color: '#f0d090', bg: '#f0d09010', border: '#f0d09038', icon: 'ri-megaphone-fill', label: '系統通知' };
  }
  if (message.includes('獲得') && (message.includes('+') || message.includes('-'))) {
    return { color: '#d4a843', bg: '#d4a84312', border: '#d4a84335', icon: 'ri-trophy-line', label: '積分' };
  }
  if (message.includes('解鎖') || message.includes('鎖定') || message.includes('已完成') || message.includes('開始了')) {
    return {
      color: '#7ec8a0', bg: '#7ec8a012', border: '#7ec8a035',
      icon: message.includes('已完成') ? 'ri-checkbox-circle-line'
          : message.includes('開始了') ? 'ri-loader-4-line'
          : message.includes('解鎖') ? 'ri-lock-unlock-line' : 'ri-lock-line',
      label: '任務',
    };
  }
  return {
    color: '#a78bfa', bg: '#a78bfa12', border: '#a78bfa35',
    icon: message.includes('開始') ? 'ri-sword-line'
        : message.includes('加入') ? 'ri-user-add-line'
        : message.includes('配對') || message.includes('任務開始') ? 'ri-spy-line' : 'ri-sparkling-2-line',
    label: '事件',
  };
}

// ── Extract task title from 「...」 ──────────────────────────────────────────
function extractTaskTitle(message: string): string | null {
  const match = message.match(/「(.+?)」/);
  return match ? match[1] : null;
}

// ── System comment card ───────────────────────────────────────────────────────
function SysCommentCard({
  comment, isNewest,
}: {
  comment: { id: string; message: string; displayName: string; createdAt: number };
  isNewest: boolean;
}) {
  const sty = getSysCommentStyle(comment.message, comment.displayName);
  const opacity = getSysOpacity(comment.createdAt);
  const isUnlockMsg = comment.message.includes('解鎖');
  const isCustomTaskMsg = comment.message.includes('臨時任務');
  const isCustomTaskDone = isCustomTaskMsg && comment.message.includes('已完成');
  const isNavigable = isUnlockMsg || isCustomTaskMsg;
  const navHint = isCustomTaskDone ? '點擊領獎 →' : '點擊查看 →';

  const handleNavigate = () => {
    // Scroll to schedule section
    const el = document.getElementById('schedule');
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 8, behavior: 'smooth' });

    // If it's a custom task message, dispatch event with task title for day switching
    if (isCustomTaskMsg) {
      const taskTitle = extractTaskTitle(comment.message);
      if (taskTitle) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('srs-navigate-schedule', { detail: { taskTitle } }));
        }, 400);
      }
    }
  };

  return (
    <div
      role={isNavigable ? 'button' : undefined}
      tabIndex={isNavigable ? 0 : undefined}
      onClick={isNavigable ? handleNavigate : undefined}
      onTouchEnd={isNavigable ? e => { e.preventDefault(); handleNavigate(); } : undefined}
      onKeyDown={isNavigable ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNavigate(); } } : undefined}
      className={`relative flex items-center gap-2.5 rounded-xl px-3 py-2.5 overflow-hidden${isNewest ? ' comment-enter' : ''}${isNavigable ? ' cursor-pointer' : ''}`}
      style={{
        background: `linear-gradient(135deg, ${sty.bg}, ${sty.color}06)`,
        border: `1px solid ${sty.border}`,
        boxShadow: isNewest ? `0 0 16px ${sty.color}20` : 'none',
        opacity,
        transition: 'opacity 0.8s, box-shadow 0.2s',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        userSelect: 'none',
      }}
    >
      {isNewest && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl opacity-40">
          <div style={{ background: `linear-gradient(90deg, transparent, ${sty.color}25, transparent)`, height: '100%', animation: 'sysSweep 1.8s ease-out', animationFillMode: 'forwards' }} />
        </div>
      )}
      <div
        className="w-7 h-7 flex items-center justify-center rounded-full shrink-0"
        style={{ background: `${sty.color}22`, border: `1.5px solid ${sty.color}40`, boxShadow: `0 0 8px ${sty.color}30` }}
      >
        <i className={`${sty.icon} text-xs`} style={{ color: sty.color }} />
      </div>
      <p className="text-xs flex-1 min-w-0 leading-snug font-semibold"
        style={{ color: `${sty.color}ee`, fontFamily: FONT_BODY, fontSize: '0.78rem' }}>
        {comment.message}
        {isNavigable && (
          <span className="ml-1.5 text-[9px] font-black tracking-wide align-middle" style={{ color: `${sty.color}80` }}>
            {navHint}
          </span>
        )}
      </p>
      <div className="flex flex-col items-end gap-0.5 shrink-0">
        <span className="text-[9px] px-1.5 py-px rounded-full font-black tracking-wide whitespace-nowrap"
          style={{ background: `${sty.color}22`, color: sty.color, border: `1px solid ${sty.color}35`, fontFamily: FONT_BODY }}>
          {sty.label}
        </span>
        <span style={{ color: '#2a2a2a', fontSize: '9px', fontFamily: FONT_HUD }} className="tabular-nums">
          {formatTime(comment.createdAt)}
        </span>
      </div>
    </div>
  );
}

// ── User comment card ─────────────────────────────────────────────────────────
function UserCommentCard({
  comment, isNewest, members,
}: {
  comment: { id: string; memberId?: string; displayName: string; message: string; team?: string; createdAt: number };
  isNewest: boolean;
  members: { id: string; avatarUrl?: string; name: string }[];
}) {
  const isBlack = comment.team === 'black';
  const isWhite = comment.team === 'white';
  const teamColor = isBlack ? '#d4a843' : isWhite ? '#e2e2e2' : '#888';
  const teamLabel = isBlack ? '黑隊' : isWhite ? '白隊' : null;
  const initials = comment.displayName.charAt(0).toUpperCase();
  const commenterMember = comment.memberId ? members.find(m => m.id === comment.memberId) : null;

  return (
    <div
      className={`relative flex items-start gap-2.5 rounded-xl px-3 py-2.5 overflow-hidden${isNewest ? ' comment-enter' : ''}`}
      style={{
        background: isNewest ? `linear-gradient(135deg, #d4a84309, ${teamColor}07)` : '#0b0d10',
        border: `1px solid ${isNewest ? '#d4a84335' : '#d4a84312'}`,
        boxShadow: isNewest ? '0 0 18px rgba(212,168,67,0.12)' : 'none',
        transition: 'all 0.3s',
      }}
    >
      <div className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-xl"
        style={{ background: isNewest ? `linear-gradient(180deg, #d4a843aa, ${teamColor}60, transparent)` : `linear-gradient(180deg, #d4a84330, transparent)` }} />

      {commenterMember?.avatarUrl ? (
        <img src={commenterMember.avatarUrl} alt={commenterMember.name}
          className="w-8 h-8 rounded-full object-cover object-top shrink-0 ml-1"
          style={{ border: `2px solid ${teamColor}50`, boxShadow: `0 0 8px ${teamColor}25` }} />
      ) : (
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 ml-1 text-xs font-black"
          style={{ background: `radial-gradient(circle at 35% 35%, ${teamColor}28, ${teamColor}08)`, color: teamColor, border: `2px solid ${teamColor}45`, fontFamily: FONT_BODY }}>
          {initials}
        </div>
      )}

      <div className="flex-1 min-w-0 pr-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="font-black truncate" style={{ fontSize: '0.78rem', color: isNewest ? '#e8e8e8' : '#888', fontFamily: FONT_BODY, letterSpacing: '0.04em' }}>
            {comment.displayName}
          </span>
          {teamLabel && (
            <span className="font-black shrink-0 tracking-widest uppercase px-1.5 py-px rounded-full"
              style={{ fontSize: '9px', background: `${teamColor}18`, color: teamColor, border: `1px solid ${teamColor}30`, fontFamily: FONT_BODY }}>
              {teamLabel}
            </span>
          )}
          <span className="ml-auto shrink-0 tabular-nums" style={{ fontSize: '9px', color: isNewest ? '#554e3a' : '#252525', fontFamily: FONT_HUD }}>
            {formatTime(comment.createdAt)}
          </span>
        </div>
        <p className="leading-relaxed break-words"
          style={{ fontSize: '0.78rem', fontWeight: isNewest ? 500 : 400, color: isNewest ? '#e8dcc0' : '#55534a', fontFamily: FONT_BODY, letterSpacing: '0.01em' }}>
          {comment.message}
        </p>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function LiveScoreBoard() {
  const {
    selectedTeam,
    setShowMemberModal,
    setPendingTeam,
    currentMember,
    gameStartedAt,
    members,
  } = useScore();

  const { comments } = useComments();
  const commentsListRef = useRef<HTMLDivElement>(null);
  const onlineCount = useOnlineCount();
  void useAnimatedNumber(0);

  const [tick, setTick] = useState(0);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  // ── 聊天通知靜音開關（持久化至 localStorage）────────────────────────────
  const [chatNotifMuted, setChatNotifMuted] = useState(
    () => localStorage.getItem('srs_chat_notif_muted') === '1'
  );
  const toggleChatNotif = () => {
    const next = !chatNotifMuted;
    setChatNotifMuted(next);
    if (next) {
      localStorage.setItem('srs_chat_notif_muted', '1');
    } else {
      localStorage.removeItem('srs_chat_notif_muted');
    }
    window.dispatchEvent(new CustomEvent('srs-chat-notif-mute', { detail: { muted: next } }));
  };

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const visibleComments = (() => {
    void tick;
    return comments.filter(c => c.memberId !== 'system' || !isSysExpired(c.createdAt));
  })();

  // 偵測是否捲離頂部（最新訊息在頂部）
  const handleScroll = useCallback(() => {
    if (!commentsListRef.current) return;
    setShowScrollBtn(commentsListRef.current.scrollTop > 80);
  }, []);

  // 新訊息進來時若在頂部附近則自動捲回頂部
  const prevLengthRef = useRef(comments.length);
  useEffect(() => {
    const el = commentsListRef.current;
    if (!el) return;
    // 只有在頂部附近（< 120px）才自動跟回去
    if (el.scrollTop < 120) {
      el.scrollTop = 0;
    }
    prevLengthRef.current = comments.length;
  }, [comments.length]);

  const scrollToLatest = useCallback(() => {
    if (commentsListRef.current) {
      commentsListRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const timerDisplay = (() => {
    void tick;
    if (gameStartedAt) {
      const elapsed = Math.floor((Date.now() - gameStartedAt) / 1000);
      const h = String(Math.floor(elapsed / 3600)).padStart(2, '0');
      const m = String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0');
      const s = String(elapsed % 60).padStart(2, '0');
      return `${h}:${m}:${s}`;
    }
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
  })();

  return (
    <section id="live" className="relative overflow-hidden py-8 sm:py-8 px-5 sm:px-6 border-b border-[#1a1a1a]" style={{ background: '#06060f' }}>

      {/* BG decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, rgba(212,168,67,0.045) 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.4), transparent)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.15), transparent)' }} />
        <div className="absolute top-4 left-4 w-5 h-5 border-l border-t border-[#d4a843]/25" />
        <div className="absolute top-4 right-4 w-5 h-5 border-r border-t border-[#d4a843]/20" />
        <div className="absolute bottom-4 left-4 w-5 h-5 border-l border-b border-[#d4a843]/15" />
        <div className="absolute bottom-4 right-4 w-5 h-5 border-r border-b border-[#d4a843]/15" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 opacity-20 rounded-full blur-3xl" style={{ background: 'radial-gradient(ellipse, rgba(212,168,67,0.5) 0%, transparent 70%)' }} />
      </div>

      <div className="max-w-5xl mx-auto">

        {/* Status bar */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex flex-col items-start gap-1">
              <span
                className="flex items-center gap-1.5 bg-[#7ec8a0]/10 border border-[#7ec8a0]/25 text-[#7ec8a0] font-bold px-2.5 py-1 rounded-full whitespace-nowrap"
                style={{ fontSize: '0.72rem', fontFamily: FONT_BODY }}
              >
                <span className="relative flex h-1.5 w-1.5 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7ec8a0] opacity-60" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#7ec8a0]" />
                </span>
                {onlineCount} 人在線
              </span>
              <div className="flex items-center gap-2">
                {gameStartedAt ? (
                  <span
                    className="flex items-center gap-1.5 bg-[#e05555]/15 border border-[#e05555]/30 text-[#e05555] font-bold px-3 py-1 rounded-full tracking-widest uppercase whitespace-nowrap"
                    style={{ fontSize: '0.68rem', fontFamily: FONT_DECO }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#e05555] animate-pulse inline-block" />
                    LIVE
                  </span>
                ) : (
                  <span
                    className="flex items-center gap-1.5 bg-[#555]/20 border border-[#555]/30 text-[#666] font-bold px-3 py-1 rounded-full tracking-widest uppercase whitespace-nowrap"
                    style={{ fontSize: '0.68rem', fontFamily: FONT_DECO }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#555] inline-block" />
                    WAITING
                  </span>
                )}
                <span className="text-[#444] text-sm tabular-nums" style={{ fontFamily: FONT_HUD }}>{timerDisplay}</span>
              </div>
            </div>
          </div>

          {/* Member bar */}
          {selectedTeam && (
            <button
              onClick={() => { setPendingTeam(selectedTeam); setShowMemberModal(true); }}
              className="w-full flex items-center gap-3 px-5 py-4 rounded-xl cursor-pointer transition-all active:scale-[0.98] hover:opacity-90"
              style={{
                background: selectedTeam === 'black' ? 'linear-gradient(135deg, #d4a84322, #d4a84310)' : 'linear-gradient(135deg, #e0e0e022, #e0e0e010)',
                border: `1.5px solid ${selectedTeam === 'black' ? '#d4a84350' : '#e0e0e040'}`,
                boxShadow: selectedTeam === 'black' ? '0 0 20px #d4a84318' : '0 0 20px #e0e0e012',
              }}
            >
              {currentMember?.avatarUrl ? (
                <img src={currentMember.avatarUrl} alt={currentMember.name}
                  className="w-11 h-11 rounded-full border-2 object-cover object-top shrink-0"
                  style={{ borderColor: selectedTeam === 'black' ? '#d4a84360' : '#e0e0e050' }} />
              ) : (
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-base font-black shrink-0"
                  style={{
                    background: selectedTeam === 'black' ? '#d4a84320' : '#e0e0e018',
                    color: selectedTeam === 'black' ? '#d4a843' : '#e0e0e0',
                    border: `1.5px solid ${selectedTeam === 'black' ? '#d4a84360' : '#e0e0e050'}`,
                    fontFamily: FONT_BODY,
                  }}>
                  {currentMember ? currentMember.name.charAt(0).toUpperCase() : <i className="ri-user-line" />}
                </div>
              )}
              <div className="flex-1 text-left min-w-0">
                <p className="text-[#555] text-[10px] tracking-widest uppercase mb-0.5" style={{ fontFamily: FONT_DECO }}>
                  {selectedTeam === 'black' ? 'BLACK TEAM' : 'WHITE TEAM'}
                </p>
                <p className="text-sm font-black truncate" style={{ color: selectedTeam === 'black' ? '#d4a843' : '#e0e0e0', fontFamily: FONT_BODY }}>
                  {currentMember ? currentMember.name : '尚未設定名稱'}
                </p>
              </div>
              <div className="w-9 h-9 flex items-center justify-center rounded-full shrink-0"
                style={{ background: selectedTeam === 'black' ? '#d4a84320' : '#e0e0e015', border: `1px solid ${selectedTeam === 'black' ? '#d4a84340' : '#e0e0e030'}` }}>
                <i className="ri-pencil-fill text-base" style={{ color: selectedTeam === 'black' ? '#d4a843' : '#e0e0e0' }} />
              </div>
            </button>
          )}
        </div>

        {/* Comment board */}
        <div
          id="live-comments"
          className="relative rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(160deg, #0d0b08 0%, #090a10 60%, #0c0b14 100%)',
            border: '1.5px solid rgba(212,168,67,0.30)',
            boxShadow: '0 0 0 1px rgba(212,168,67,0.08), 0 0 40px rgba(212,168,67,0.10), inset 0 0 30px rgba(212,168,67,0.03)',
          }}
        >
          <div className="absolute inset-0 pointer-events-none z-0 opacity-[0.022]"
            style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.5) 3px, rgba(255,255,255,0.5) 4px)' }} />

          {/* Side neon lines */}
          <div className="absolute top-0 bottom-0 left-0 w-px pointer-events-none z-10"
            style={{ background: 'linear-gradient(180deg, transparent, rgba(212,168,67,0.4) 30%, rgba(212,168,67,0.4) 70%, transparent)' }} />
          <div className="absolute top-0 bottom-0 right-0 w-px pointer-events-none z-10"
            style={{ background: 'linear-gradient(180deg, transparent, rgba(212,168,67,0.2) 30%, rgba(212,168,67,0.2) 70%, transparent)' }} />

          {/* Board header */}
          <div className="relative px-4 py-2.5 flex items-center justify-between z-10"
            style={{ borderBottom: '1px solid rgba(212,168,67,0.15)', background: 'linear-gradient(90deg, rgba(212,168,67,0.10) 0%, #090a10 60%)' }}>
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg"
                style={{ background: 'rgba(212,168,67,0.14)', border: '1px solid rgba(212,168,67,0.40)', boxShadow: '0 0 14px rgba(212,168,67,0.35), 0 0 28px rgba(212,168,67,0.12)' }}>
                <i className="ri-chat-voice-fill text-xs" style={{ color: '#d4a843' }} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-black whitespace-nowrap"
                    style={{ fontSize: '0.82rem', color: '#d4a843', fontFamily: FONT_BODY, textShadow: '0 0 12px rgba(212,168,67,0.9), 0 0 28px rgba(212,168,67,0.45), 0 0 55px rgba(212,168,67,0.18)', letterSpacing: '0.1em' }}>
                    戰場留言板
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-px">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7ec8a0] opacity-70" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#7ec8a0]" />
                  </span>
                  <span className="font-bold tracking-widest" style={{ fontSize: '9px', color: '#7ec8a0', fontFamily: FONT_BODY }}>即時同步中</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* 訊息數量 */}
              {visibleComments.length > 0 && (
                <span className="font-black tabular-nums px-2 py-0.5 rounded-full"
                  style={{ fontSize: '10px', background: 'rgba(212,168,67,0.08)', color: 'rgba(212,168,67,0.5)', border: '1px solid rgba(212,168,67,0.2)', fontFamily: FONT_HUD }}>
                  {visibleComments.length}
                </span>
              )}
              {/* 聊天通知開關 */}
              <button
                onClick={toggleChatNotif}
                title={chatNotifMuted ? '開啟聊天通知' : '關閉聊天通知'}
                className="flex items-center gap-1.5 cursor-pointer select-none"
              >
                {/* 標籤 */}
                <span
                  className="font-black tracking-widest uppercase transition-colors duration-300 text-right inline-block"
                  style={{
                    fontSize: '7px',
                    width: 34,
                    color: chatNotifMuted ? 'rgba(230,70,70,0.75)' : 'rgba(255,255,255,0.45)',
                    fontFamily: FONT_HUD,
                  }}
                >
                  {chatNotifMuted ? 'MUTED' : 'NOTIF'}
                </span>
                {/* Track */}
                <div
                  className="relative rounded-full transition-all duration-300"
                  style={{
                    width: 34,
                    height: 18,
                    background: chatNotifMuted
                      ? 'rgba(220,50,50,0.18)'
                      : 'rgba(255,255,255,0.10)',
                    border: `1.5px solid ${chatNotifMuted ? 'rgba(220,60,60,0.55)' : 'rgba(255,255,255,0.30)'}`,
                    boxShadow: chatNotifMuted
                      ? '0 0 8px rgba(220,50,50,0.2) inset'
                      : '0 0 6px rgba(255,255,255,0.06) inset',
                  }}
                >
                  {/* Knob */}
                  <div
                    className="absolute top-[2px] flex items-center justify-center rounded-full transition-all duration-300"
                    style={{
                      width: 14,
                      height: 14,
                      left: chatNotifMuted ? 2 : 16,
                      background: chatNotifMuted
                        ? 'linear-gradient(135deg,#e04040,#c02020)'
                        : 'linear-gradient(135deg,rgba(255,255,255,0.92),rgba(210,210,210,0.8))',
                      boxShadow: chatNotifMuted
                        ? '0 0 7px rgba(220,50,50,0.7)'
                        : '0 0 6px rgba(255,255,255,0.5)',
                    }}
                  >
                    <i
                      className={chatNotifMuted ? 'ri-notification-off-fill' : 'ri-notification-3-fill'}
                      style={{
                        fontSize: '8px',
                        color: chatNotifMuted ? '#fff' : 'rgba(30,20,0,0.75)',
                        lineHeight: 1,
                      }}
                    />
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* ── Comment list（固定高度，可滑動）── */}
          <div className="relative z-10">
            <div
              ref={commentsListRef}
              onScroll={handleScroll}
              className="overflow-y-auto px-3 py-3 space-y-2 comments-scroll"
              style={{ height: '11rem' }}
            >
              {visibleComments.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center gap-3 select-none">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(212,168,67,0.14), rgba(212,168,67,0.05))', border: '1.5px dashed rgba(212,168,67,0.28)', boxShadow: '0 0 20px rgba(212,168,67,0.12)' }}>
                    <i className="ri-sword-line text-2xl" style={{ color: 'rgba(212,168,67,0.45)' }} />
                  </div>
                  <div className="text-center">
                    <p className="tracking-widest" style={{ color: '#333', fontSize: '1rem' }}>此地無聲</p>
                    <p style={{ color: '#252525', fontSize: '10px', fontFamily: FONT_BODY }} className="mt-1">第一個發言的人，將名留戰場</p>
                  </div>
                </div>
              ) : (
                visibleComments.map((comment, idx) => {
                  const isNewest = idx === 0;
                  if (comment.memberId === 'system') {
                    return <SysCommentCard key={comment.id} comment={comment} isNewest={isNewest} />;
                  }
                  return <UserCommentCard key={comment.id} comment={comment} isNewest={isNewest} members={members} />;
                })
              )}
            </div>

            {/* 回到最新訊息浮動按鈕 */}
            {showScrollBtn && (
              <button
                onClick={scrollToLatest}
                className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-full cursor-pointer transition-all active:scale-95 whitespace-nowrap"
                style={{
                  background: 'linear-gradient(135deg, #d4a84322, #d4a84312)',
                  border: '1px solid rgba(212,168,67,0.45)',
                  boxShadow: '0 2px 16px rgba(212,168,67,0.25)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <i className="ri-arrow-up-line text-xs" style={{ color: '#d4a843' }} />
                <span style={{ color: '#d4a843', fontSize: '10px', fontFamily: FONT_BODY }} className="font-bold">最新訊息</span>
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="relative px-5 py-2.5 flex items-center justify-center gap-2 z-10"
            style={{ borderTop: '1px solid rgba(212,168,67,0.12)', background: 'linear-gradient(90deg, #090a10, #0c0b14)' }}>
            <div className="flex items-center gap-1">
              {[0, 150, 300].map(d => (
                <div key={d} className="w-1 h-1 rounded-full"
                  style={{ background: 'rgba(212,168,67,0.45)', animation: `footerDot 1.5s ${d}ms infinite`, boxShadow: '0 0 4px rgba(212,168,67,0.5)' }} />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <i className="ri-arrow-right-down-line text-xs" style={{ color: 'rgba(212,168,67,0.35)' }} />
              <span style={{ color: '#2e2e2e', fontSize: '10px', fontFamily: FONT_BODY }} className="tracking-wide">
                點擊右下角「我要說話」發言
              </span>
            </div>
            <div className="flex items-center gap-1">
              {[0, 150, 300].map(d => (
                <div key={d} className="w-1 h-1 rounded-full"
                  style={{ background: 'rgba(212,168,67,0.45)', animation: `footerDot 1.5s ${d}ms infinite`, boxShadow: '0 0 4px rgba(212,168,67,0.5)' }} />
              ))}
            </div>
          </div>
        </div>

      </div>

      <style>{`
        .comments-scroll::-webkit-scrollbar { width: 3px; }
        .comments-scroll::-webkit-scrollbar-track { background: transparent; }
        .comments-scroll::-webkit-scrollbar-thumb { background: rgba(212,168,67,0.25); border-radius: 99px; }
        .comments-scroll::-webkit-scrollbar-thumb:hover { background: rgba(212,168,67,0.45); }
        .comment-enter { animation: commentEnter 0.38s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
        @keyframes commentEnter {
          from { opacity: 0; transform: translateY(-8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes sysSweep {
          from { transform: translateX(-100%); }
          to   { transform: translateX(300%); }
        }
        @keyframes footerDot {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50%       { opacity: 1;   transform: scale(1.5); }
        }
      `}</style>
    </section>
  );
}
